/**
 * `yyyy-MM-dd` helpers that stay in local time.
 *
 * `new Date('2026-09-19')` is parsed as **UTC** midnight, so doing the arithmetic on a `Date`
 * and formatting the result locally shifts the day in every negative UTC offset. Working on the
 * string parts avoids that entirely (EB-4209 review run 2).
 */

/** Today as `yyyy-MM-dd` in the local timezone. */
export function todayIso(): string {
  return toLocalIsoDay(new Date());
}

/** `date` (`yyyy-MM-dd`) plus `days`, as `yyyy-MM-dd`. Month/year rollover included. */
export function addDaysIso(date: string, days: number): string {
  const [year, month, day] = date.split('-').map(Number);
  return toLocalIsoDay(new Date(year, month - 1, day + days));
}

/**
 * `date` as `yyyy-MM-dd` in the local timezone.
 * @ticket EB-4153
 */
export function toLocalIsoDay(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * The later of two `yyyy-MM-dd` dates. A missing/empty side is ignored, so a caller can pass
 * an optional bound without a guard. Both sides are compared lexicographically, which is a
 * date comparison for this format.
 */
export function maxIsoDate(a: string, b: string | null | undefined): string {
  if (!b) return a;
  if (!a) return b;
  return b > a ? b : a;
}

/**
 * `date` (`yyyy-MM-dd`) plus `months`, as `yyyy-MM-dd`.
 *
 * Deliberately reproduces JavaScript's month-overflow, which is what old CMT's
 * `dateInOneMonth`/`dateInTwoMonth` (`utilityService.js:209-223`) get from
 * `Date.setMonth()`: 31 January plus one month lands on 3 March, not on 28/29 February.
 * Clamping to the month end would be the friendlier rule but a different one, and these
 * bounds exist to match old CMT.
 */
export function addMonthsIso(date: string, months: number): string {
  const [year, month, day] = date.split('-').map(Number);
  const shifted = new Date(year, month - 1, day);
  shifted.setMonth(shifted.getMonth() + months);
  return toLocalIsoDay(shifted);
}
